import React, { useState } from 'react';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Eye,
  Star
} from 'lucide-react';
import { Button, SearchSelect, DataTable } from '../../components/shared';
import { useAdminBlogPosts } from '../../hooks/useAdminBlogPosts';
import { useToastContext } from '../../contexts/ToastContext';
import CreateBlogPostModal from '../../components/admin/CreateBlogPostModal';

const AdminBlogPosts = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const { success, error: showError } = useToastContext();

  const {
    posts,
    isLoading,
    error,
    filters,
    handleFilterChange,
    statistics,
    createPost,
    isCreating,
    updatePost,
    isUpdating,
    toggleStatus,
    isToggling,
    deletePost,
    isDeleting
  } = useAdminBlogPosts();

  const columns = [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (post) => (
        <div>
          <p className="text-sm font-medium text-gray-900 truncate">
            {post.title || 'N/A'}
          </p>
          {post.is_featured && (
            <span className="inline-flex items-center gap-1 text-xs text-yellow-600 mt-1">
              <Star className="w-3 h-3 fill-current" />
              Featured
            </span>
          )}
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (post) => (
        <span className="text-sm text-gray-900 capitalize">
          {post.category || 'N/A'}
        </span>
      )
    },
    {
      key: 'author_name',
      label: 'Author',
      sortable: true,
      width: 'w-40',
      render: (post) => (
        <span className="text-sm text-gray-900">
          {post.author_name || 'N/A'}
        </span>
      )
    },
    {
      key: 'views',
      label: 'Views',
      sortable: true,
      width: 'w-24',
      render: (post) => (
        <span className="text-sm text-gray-900 flex items-center gap-1">
          <Eye className="w-4 h-4" />
          {post.views || 0}
        </span>
      )
    },
    {
      key: 'is_published',
      label: 'Status',
      sortable: true,
      width: 'w-32',
      render: (post) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          post.is_published 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {post.is_published ? 'Published' : 'Draft'}
        </span>
      )
    }
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (post) => {
        setEditingPost(post);
        setShowCreateModal(true);
      }
    },
    {
      label: post => post.is_published ? 'Unpublish' : 'Publish',
      icon: post => post.is_published ? XCircle : CheckCircle,
      variant: 'default',
      onClick: (post) => {
        toggleStatus(post.id, {
          onSuccess: () => {
            success('Status Updated', `Post ${post.is_published ? 'unpublished' : 'published'} successfully!`);
          },
          onError: (err) => showError('Status Update Failed', err.message || 'Failed to update status.')
        });
      },
      disabled: isToggling
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (post) => {
        if (window.confirm('Are you sure you want to delete this blog post?')) {
          deletePost(post.id, {
            onSuccess: () => success('Post Deleted', 'Blog post deleted successfully!'),
            onError: (err) => showError('Delete Failed', err.message || 'Failed to delete post.')
          });
        }
      },
      disabled: isDeleting
    }
  ];

  const handleSave = (data) => {
    if (editingPost) {
      updatePost(
        { id: editingPost.id, data },
        {
          onSuccess: () => {
            success('Post Updated', 'Blog post updated successfully!');
            setShowCreateModal(false);
            setEditingPost(null);
          },
          onError: (err) => showError('Update Failed', err.message || 'Failed to update post.')
        }
      );
    } else {
      createPost(data, {
        onSuccess: () => {
          success('Post Created', 'Blog post created successfully!');
          setShowCreateModal(false);
          setEditingPost(null);
        },
        onError: (err) => showError('Creation Failed', err.message || 'Failed to create post.')
      });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Blog Posts</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load blog posts. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6" />
                Blog Posts
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage blog posts and articles
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingPost(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Post
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Posts</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{statistics.total_posts || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Published</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{statistics.published_posts || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Drafts</p>
                <p className="text-2xl font-bold text-gray-600 mt-1">{statistics.draft_posts || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Featured</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{statistics.featured_posts || 0}</p>
              </div>
              <Star className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Views</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{statistics.total_views || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Status"
              value={filters.is_published}
              onChange={(value) => handleFilterChange('is_published', value)}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'true', label: 'Published' },
                { value: 'false', label: 'Draft' }
              ]}
            />
            <SearchSelect
              label="Featured"
              value={filters.is_featured}
              onChange={(value) => handleFilterChange('is_featured', value)}
              options={[
                { value: '', label: 'All' },
                { value: 'true', label: 'Featured' },
                { value: 'false', label: 'Not Featured' }
              ]}
            />
            <div className="flex items-end">
              <input
                type="text"
                placeholder="Search posts..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => {
                  handleFilterChange('is_published', '');
                  handleFilterChange('is_featured', '');
                  handleFilterChange('search', '');
                }}
                variant="outline"
                className="w-full h-[42px]"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={posts}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search blog posts..."
            onSearch={(searchTerm) => handleFilterChange('search', searchTerm)}
            emptyMessage="No blog posts found"
            pageSize={10}
            showPagination={true}
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateBlogPostModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditingPost(null);
          }}
          onSave={handleSave}
          post={editingPost}
          isEdit={!!editingPost}
          isLoading={isCreating || isUpdating}
        />
      )}
    </div>
  );
};

export default AdminBlogPosts;

